import { widgetApi } from "../services/api";

/**
 * Typed wrappers over the pricing endpoints exposed by raas-widgets-backend.
 *
 *   - `POST /widget/pre-quote` → indicative, non-binding estimate that fans out
 *     across every quotable payer. Used by the request flow to discover which
 *     payout methods / payers are actually available for the corridor and to
 *     capture the `capabilityKey` of the chosen option.
 *
 * The `x-link-id` header is injected by the shared `widgetApi` interceptor and
 * the `partnerId` is derived server-side from the link — the widget never sends
 * it. Shapes mirror the backend DTOs; we unwrap `{ success, data }` here.
 */

/** Which side of the transfer the pinned `amount` refers to. */
export type PreQuoteAnchor = "send" | "receive";

/** Grouping axis the engine reduces options on. */
export type PreQuoteVary = "payer" | "fundingMethod";

/** Response projection slices. Omit for legacy `{ options, summary }`. */
export type PreQuoteInclude = "options" | "summary" | "facets";

export interface PreQuoteInput {
  originCountryCode: string;
  destinationCountryCode: string;
  /** Interpreted per `anchor`: source ccy for `send`, destination for `receive`. */
  amount: number;
  /** Defaults to `send` server-side. */
  anchor?: PreQuoteAnchor;
  destinationCurrency: string;
  /** Defaults to `USD` server-side. */
  sourceCurrency?: string;
  fundingMethod?: string;
  payoutMethod?: string;
  /** Pins a catalog payer when the compose already chose one. */
  payerCode?: string;
  /** Defaults to `payer` server-side. */
  vary?: PreQuoteVary;
  /**
   * Response projection. Omit for legacy `{ options, summary }`. Pass
   * `['facets']` (or `['summary','facets']`) for pre-collapse discovery
   * without shipping the full options list.
   */
  include?: PreQuoteInclude[];
}

/**
 * A single priced pre-quote option. Public (widget) projection only — never
 * carries internal economics or guardrail flags.
 */
export interface EstimateOption {
  capabilityKey: string;
  corridorCode: string;
  payerCode: string | null;
  payoutMethod: string;
  fundingMethod: string;
  sendAmount: number;
  receiveAmount: number;
  customerFee: number;
  customerFxRate: number;
  sourceCurrency: string;
  destinationCurrency: string;
}

export interface EstimateRange {
  min: number;
  max: number;
  avg: number;
}

export interface EstimateSummary {
  count: number;
  /** Cheapest option for the end user. Absent when `count === 0`. */
  best?: EstimateOption;
  /** Most expensive offerable option. */
  worst?: EstimateOption;
  rankingMetric: "receiveAmount" | "sendAmount";
  receiveAmount?: EstimateRange;
  sendAmount?: EstimateRange;
  asOf: string;
  indicative: true;
}

/** Compact economics of the cheapest option within a facet bucket. */
export interface EstimateFacetBest {
  capabilityKey: string;
  sendAmount: number;
  receiveAmount: number;
  customerFee: number;
  customerFxRate: number;
  corridorCode: string;
}

/**
 * Pre-collapse discovery dimensions. Present only when `include` requests
 * `facets`. Every offerable funding method appears even when `vary=payer`
 * keeps only the cheapest option per payer in `options`.
 */
export interface EstimateFacets {
  fundingMethods: Array<{ fundingMethod: string; best: EstimateFacetBest }>;
  payoutMethods: Array<{ payoutMethod: string; best: EstimateFacetBest }>;
  payers: Array<{ payerCode: string; best: EstimateFacetBest }>;
  currencies: Array<{ destinationCurrency: string; best: EstimateFacetBest }>;
}

export interface EstimateResult {
  /** Absent when the caller requested a projection without `options`. */
  options?: EstimateOption[];
  /** Absent when the caller requested a projection without `summary`. */
  summary?: EstimateSummary;
  /** Absent unless `include` contains `facets`. */
  facets?: EstimateFacets;
}

interface PreQuoteResponse {
  success: boolean;
  data: EstimateResult;
}

export const getPreQuote = async (
  input: PreQuoteInput
): Promise<EstimateResult> => {
  // Pre-quote fans out across many payers; keep headroom over the default 15s
  // client timeout so a cold FX desk / wide corridor doesn't abort mid-flight.
  const { data } = await widgetApi.post<PreQuoteResponse>(
    "/widget/pre-quote",
    input,
    { timeout: 40_000 }
  );
  return data.data;
};
